import { Link, useNavigate } from '@tanstack/react-router';
import { merchantChatLink } from '@telestore/telegram-adapter/deeplink';
import { pickText } from '@telestore/contracts';
import type { ReactNode } from 'react';
import { cartCount, useCart } from '../lib/cart.js';
import { MessageCircle, ShoppingBag } from 'lucide-react';
import { useShop } from '../lib/shop-context.js';
import { isMiniApp, openTelegramLink } from '../lib/telegram.js';
import { ReportShop } from './trust.js';
import { Button } from '../components/ui/button.js';

/**
 * Page chrome. The header carries the shop's identity and the two actions a
 * customer reaches for: the cart, and "chat with the shop" — which is a link
 * to the merchant's own Telegram account, because §2 is explicit that we build
 * no chat system of our own.
 */

export function ShopHeader({ showBack }: { showBack?: boolean }) {
  const { data, locale, setLocale, t } = useShop();
  const { items } = useCart();
  const navigate = useNavigate();
  const count = cartCount(items);
  const shop = data.shop;
  const logo = data.resolvedTheme.logo ?? shop.theme.logo;

  return (
    <header
      className="sticky top-0 z-20 backdrop-blur-sm"
      style={{
        background: 'color-mix(in srgb, var(--color-bg) 88%, transparent)',
        borderBottom: '1px solid var(--color-border)',
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        {showBack ? (
          <button
            type="button"
            aria-label={t('common.back')}
            onClick={() => navigate({ to: '/s/$slug', params: { slug: shop.slug } })}
            className="-ml-1 flex size-9 items-center justify-center rounded-full"
            style={{ background: 'var(--color-surface)' }}
          >
            ‹
          </button>
        ) : null}

        <Link to="/s/$slug" params={{ slug: shop.slug }} className="flex min-w-0 flex-1 items-center gap-2">
          {logo ? (
            <img src={logo} alt="" className="size-8 rounded-full object-cover" />
          ) : null}
          <span className="truncate font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
            {shop.name}
          </span>
        </Link>

        <button
          type="button"
          onClick={() => setLocale(locale === 'km' ? 'en' : 'km')}
          className="rounded-full px-2 py-1 text-xs font-medium"
          style={{ background: 'var(--color-surface)' }}
          aria-label="Language"
        >
          {locale === 'km' ? 'EN' : 'ខ្មែរ'}
        </button>

        <Link
          to="/s/$slug/cart"
          params={{ slug: shop.slug }}
          className="relative flex size-9 items-center justify-center rounded-full"
          style={{ background: 'var(--color-surface)' }}
          aria-label={t('storefront.viewCart')}
        >
          <ShoppingBag className="size-5" aria-hidden />
          {count > 0 ? (
            <span
              className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full text-[10px] font-bold"
              style={{ background: 'var(--color-primary)', color: 'var(--color-primary-contrast)' }}
            >
              {count}
            </span>
          ) : null}
        </Link>
      </div>
    </header>
  );
}

export function ShopFooter() {
  const { data, locale, t } = useShop();
  const shop = data.shop;
  const policy = pickText(shop.returnPolicy, locale);

  return (
    <footer className="mt-12 px-4 pb-24 pt-8 text-sm" style={{ borderTop: '1px solid var(--color-border)' }}>
      {shop.description ? <p className="opacity-75">{shop.description}</p> : null}

      {policy ? (
        <details className="mt-4">
          <summary className="cursor-pointer font-medium">{t('bot.returnPolicyTitle')}</summary>
          <p className="mt-2 whitespace-pre-line opacity-75">{policy}</p>
        </details>
      ) : null}

      {shop.contactTelegramUsername ? (
        <Button variant="ghost"
          className="mt-6"
          onClick={() => openTelegramLink(merchantChatLink(shop.contactTelegramUsername!))}
        >
          <MessageCircle />
          {t('storefront.chatWithShop')}
        </Button>
      ) : null}

      {shop.contactPhone ? (
        <a href={`tel:${shop.contactPhone}`} className="mt-3 block text-center opacity-70">
          {shop.contactPhone}
        </a>
      ) : null}

      {/* §6.7: the report mechanism lives where somebody looking to complain already is. */}
      <ReportShop />

      <p className="mt-8 text-center text-xs opacity-45">Powered by TeleStore</p>
    </footer>
  );
}

/**
 * Sticky bottom action. In a Mini App the primary action lives on Telegram's
 * own MainButton instead, so this only renders on plain web — the two must
 * never both be visible.
 */
export function StickyBar({ children }: { children: ReactNode }) {
  if (isMiniApp()) return null;
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 mx-auto w-full px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3"
      style={{
        // Match the centred app column so the bar does not span a desktop
        // window while the content sits in the middle.
        maxWidth: '520px',
        background: 'color-mix(in srgb, var(--color-bg) 94%, transparent)',
        borderTop: '1px solid var(--color-border)',
      }}
    >
      {children}
    </div>
  );
}
